import type { AnalyticsEventMap, BaseEventData } from '@cowbell/data/analytics';
import { getPersonaLabel } from '@cowbell/data/analytics';
import { PlatformPersonas } from '@cowbell/data/statics';
import React, { useCallback } from 'react';

import { AnalyticsContext } from './analytics.context';

import { useActor } from '../../components/hooks/useActor';
import { usePersona } from '../../components/hooks/usePersona';

export const useAnalytics = ({
  skipPersona = false,
}: { skipPersona?: boolean } = {}) => {
  const context = React.useContext(AnalyticsContext);

  const actor = useActor();
  const persona = usePersona({ skip: skipPersona });
  const personaLabel = getPersonaLabel(persona.type as PlatformPersonas);

  // actor/persona fields are injected here — callers must not pass them directly
  const wrappedTrack = useCallback(
    <K extends keyof AnalyticsEventMap>(
      eventName: K,
      data: Omit<AnalyticsEventMap[K], keyof BaseEventData>
    ) => {
      const enrichedData = {
        ...data,
        actorId: actor.id,
        actorEmail: actor.email,
        actorName: actor.firstName + ' ' + actor.lastName,
        actorAccountType: actor.accountType,
        actorAccountId: actor.accountId,
        persona: personaLabel,
      } as AnalyticsEventMap[K];

      context.track(eventName, enrichedData);
    },
    [context, actor, personaLabel]
  );

  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }

  return {
    track: wrappedTrack,
    identify: context.identify.bind(context),
  };
};
